import React from "react";
import { Button } from "@proda-ai/ui-kit";

import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";

import { formatNumber } from "@/ui/organisms/utils";

export const testIds = {
    panel: "GridCellEditPanel",
    clear: "GridCellEditPanel-clear",
    revert: "GridCellEditPanel-revert",
};

export type GridCellEditPanelProps = {
    accountName: string;
    // Header of the selected cell's column (e.g. "01/2024").
    columnHeader: string | null;
    // Values are in hundredths (formatNumber divides by 100).
    original: number | null;
    current: number | null;
    isEdited: boolean;
    onClear: () => void;
    onRevert: () => void;
};

const Row: React.FC<{ label: string; value: number | null }> = ({
    label,
    value,
}) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
        <Typography variant="body2" sx={{ color: "var(--proda-black-10)" }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatNumber(value)}
        </Typography>
    </Box>
);

const GridCellEditPanel: React.FC<GridCellEditPanelProps> = ({
    accountName,
    columnHeader,
    original,
    current,
    isEdited,
    onClear,
    onRevert,
}) => (
    <Box
        data-testid={testIds.panel}
        sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "16px",
            margin: "16px",
            backgroundColor: "var(--proda-light-blue-l95)",
            border: "1px solid var(--proda-light-blue-120)",
            borderRadius: "8px",
        }}
    >
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {accountName}
        </Typography>
        {columnHeader !== null && (
            <Typography
                variant="body2"
                sx={{ color: "var(--proda-black-10)", marginTop: "-4px" }}
            >
                {columnHeader}
            </Typography>
        )}
        <Row label="Original" value={isEdited ? original : current} />
        <Row label="Current" value={current} />
        <Box sx={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <Button
                data-testid={testIds.clear}
                pattern="secondary"
                size="medium"
                disabled={current === null}
                onClick={onClear}
            >
                Clear
            </Button>
            <Button
                data-testid={testIds.revert}
                pattern="danger"
                isGhost={true}
                size="medium"
                leadingIcon="rotate-left"
                disabled={!isEdited}
                onClick={onRevert}
            >
                Revert to original
            </Button>
        </Box>
    </Box>
);

export default GridCellEditPanel;
